<template>
	<section id="notification-bell-section">
		<div class="notification-bell-container">
			<div v-if="isLoading"></div>
			<div v-else>
				<button class="bell-button" @click.stop="toggleNotifications" id="menu_show_notifications_click">
					<div v-if="nblistingLengthStatusNull == 0">
						<bell />
					</div>
					<div class="notification-bell" v-else-if="nblistingLengthStatusNull > 0">
						<bell_alert class="btn-bell" />
						<div class="my-btn-border"></div>
					</div>
				</button>
				
				<div class="notifications-popup" v-if="showNotifications">
					<div class="notification-list">
						<div v-if="listingLength === 0" class="no-notifications-message">No notification available.</div>
						<div v-else class="notification-item" v-for="(notification, index) in dedupedNotificationListing.slice(0, displayedNotifications)" :key="notification.id">
							<a v-if="notification.redirect_url.startsWith('/')" :href="notification.redirect_url" class="notification-link" @click="handleNotificationClick(notification)" id="menu_see_notification_detail_click">
								<div class="notification-content">
									<h4 class="notification-title">{{ notification.title }}</h4>
									<p class="notification-description">{{ notification.description }}</p>
								</div>
							</a>
							<a v-else-if="notification.redirect_url.startsWith('()')" href="#" class="notification-link" @click.prevent="executeFunction(notification.redirect_url.slice(2), notification, notification.id)">
								<div class="notification-content">
									<h4 class="notification-title">{{ notification.title }}</h4>
									<p class="notification-description">{{ notification.description }}</p>
								</div>
							</a>
							<span class="notification-unread-indicator" v-if="notification.status == 0"></span>
						</div>
					</div>
					<div class="notification-actions" v-if="listingLength > 0">
						<a v-if="listingLength > displayedNotifications" href="#" class="view-more-link" @click.prevent="loadMoreNotifications">View more</a>
						<a v-if="nblistingLengthStatusNull > 0" href="#" class="mark-all-read-link" @click.prevent="markAllAsRead">Mark all as read</a>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { defineEmits } from 'vue';
import axios from 'axios';

const emit = defineEmits(['openDevLogsModal']);

import bell from '../icons/svg/bell_icon.vue';
import bell_alert from '../icons/svg/bell_alert_icon.vue';
import { useNotificationBellStore } from '@/store/global/notifications_listing.js';
import { storeToRefs } from 'pinia';
import { useMarketStore } from '@/store/global/market';
import { useRoute } from 'vue-router';

const route = useRoute();
const marketStore = useMarketStore();
const { selectedMarket } = storeToRefs(marketStore);
const showNotifications = ref(false);
const useNotifications = useNotificationBellStore();
const { listing, isLoading } = storeToRefs(useNotifications);
const notificationListing = computed(() => listing.value || []);

const dedupedNotificationListing = computed(() => {
	const raw = notificationListing.value;
	if (!raw.length) return [];

	const seen = new Map();
	for (const n of raw) {
		const key = `${n.redirect_url ?? ''}||${n.title ?? ''}||${n.description ?? ''}`;
		const existing = seen.get(key);
		if (!existing) {
			seen.set(key, { ...n });
		} else if (n.status !== 0) {
			existing.status = 1;
		}
	}

	return Array.from(seen.values());
});

const listingLength = computed(() => dedupedNotificationListing.value.length);
const nblistingLengthStatusNull = computed(() => dedupedNotificationListing.value.filter(item => item.status == 0).length);

const displayedNotifications = ref(3);
const isProcessingNotifications = ref(false);

function toggleNotifications() {
	showNotifications.value = !showNotifications.value;
}

function handleNotificationClick(notification) {
	useNotifications.markAsRead(notification.id, selectedMarket.value);
	if (notification.url) {
		window.open(notification.url, '_blank');
	}
}

watch(
	() => listing.value,
	(newListing) => {
		if (isProcessingNotifications.value) {
			return;
		}
		
		if (newListing && newListing.length > 0) {
			const notificationsToMark = newListing.filter(
				(notification) => notification.title.includes('0 recommendations') && notification.status === 0
			);
			
			if (notificationsToMark.length > 0) {
				isProcessingNotifications.value = true;
				Promise.all(
					notificationsToMark.map((notification) =>
						axios.post(
							'vuejs/mark_notification_as_read',
							{ notification_id: notification.id },
							{ withCredentials: true }
						)
					)
				).then(() => {
					useNotifications.fetchData(selectedMarket.value).finally(() => {
						isProcessingNotifications.value = false;
					});
				});
			}
		}
	},
	{ deep: true, immediate: true }
);

function loadMoreNotifications() {
	displayedNotifications.value += 3;
}

async function markAllAsRead() {
	const unreadNotifications = notificationListing.value.filter(
		(notification) => notification.status === 0
	);
	
	if (unreadNotifications.length === 0) {
		return;
	}
	
	if (isProcessingNotifications.value) {
		return;
	}
	
	isProcessingNotifications.value = true;
	
	try {
		await Promise.all(
			unreadNotifications.map((notification) =>
				axios.post(
					'vuejs/mark_notification_as_read',
					{ notification_id: notification.id },
					{ withCredentials: true }
				)
			)
		);
		
		await useNotifications.fetchData(selectedMarket.value);
	} finally {
		isProcessingNotifications.value = false;
	}
}

function executeFunction(functionName, notification, id) {
	if (functionName === 'OpenDevLogsModal') {
		emit('openDevLogsModal');
		useNotifications.markAsRead(id, selectedMarket.value);
	}
}

function handleClickOutside(event) {
	const notificationContainer = event.target.closest('.notification-bell-container');
	const popup = event.target.closest('.notifications-popup');
	
	if (!notificationContainer && !popup) {
		showNotifications.value = false;
	}
}

watch(showNotifications, newVal => {
	if (newVal) {
		window.addEventListener('click', handleClickOutside);
	} else {
		window.removeEventListener('click', handleClickOutside);
	}
});

const currentPath = computed(() => route.path);

watch(
	currentPath,
	(newVal, oldVal) => {
		if (newVal !== oldVal) {
			useNotifications.fetchData(selectedMarket.value);
		}
	},
	{ immediate: true },
);
</script>

<style scoped>
.bell-button {
	background: none;
	border: none;
	cursor: pointer;
	position: relative;
	border-radius: 100%;
}

.bell-button:hover {
	background: none;
}

.notifications-popup {
	position: absolute;
	top: 80px;
	right: 20px;
	border: 1px solid var(--gris-100);
	background-color: var(--blanc);
	width: 380px;
	max-height: 400px;
	overflow: auto;
	z-index: 1000;
	border-radius: 10px;
}

.notification-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 10px;
	border-bottom: 1px solid  var(--gris-50);
	cursor: pointer;
}

.notification-content {
	flex-grow: 1;
}

.notification-title {
	margin: 0;
	margin-bottom: 0.5rem;
}

.notification-description {
	margin: 0;
	font-size: 1rem;
	font-weight: 300 !important;
}

.notification-unread-indicator {
	flex-shrink: 0;
	height: 15px;
	width: 15px;
	border-color: var(--noir);
	border-width: 2px;
	border-style: solid;
	background-color: var(--rouge-500);
	border-radius: 50%;
	margin-left: 5px;
	margin-right: 5px;
}

.notification-actions {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 1rem;
	padding: .5rem 1rem;
	border-top: 1px solid var(--gris-50);
}

.view-more-link {
	text-align: center;
	font-weight: 300 !important;
	text-decoration: none;
	color:  var(--gris-400);
}

.view-more-link:hover {
	text-decoration: underline;
}

.mark-all-read-link {
	text-align: center;
	font-weight: 300 !important;
	text-decoration: none;
	color:  var(--gris-400);
}

.mark-all-read-link:hover {
	text-decoration: underline;
}

.notification-list {
	max-height: 350px;
	overflow-y: auto;
}

.no-notifications-message {
	padding: 25px 0px 25px 0px;
	text-align: center;
	color:  var(--gris-400);
	font-weight: 300 !important;
}

.notification-link {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 10px;
	text-decoration: none;
	color: inherit;
}

.notification-link:hover {
	text-decoration: none;
}

.notification-bell {
	position: relative;
	animation-name: col;
	animation-duration: 25s;
	animation-iteration-count: infinite;
}

.btn-bell {
	animation-name: bell-ring;
	animation-duration: 25s;
	animation-iteration-count: infinite;
}

.my-btn-border {
	opacity: 0;
	position: absolute;
	border-radius: 50%;
	top: 15%;
	left: 70%;
	width: 100%;
	aspect-ratio: 1/1;
	border: 1px solid  var(--rouge-300)!important;
	animation-name: bord-pop;
	animation-duration: 25s;
	animation-iteration-count: infinite;
	box-shadow:
		2px 2px 5px var(--gris-100),
		-2px -2px 5px var(--gris-100);
}

/* @keyframes bord-pop {
	0% {
		transform: translate(-50%, -50%);
		opacity: 0;
	}

	0.66% {
		transform: translate(-50%, -50%) scale(1.6);
		opacity: 0.1;
	}

	1.33% {
		transform: translate(-50%, -50%) scale(1.9);
		opacity: 0.4;
	}

	5% {
		opacity: 0;
	}

	100% {
		transform: translate(-50%, -50%);
		opacity: 0;
	}
} */

@keyframes col {
	0% {
		transform: scale(1) translate(0, 0);
	}

	0.66% {
		transform: scale(1.1) translate(0, 0);
	}

	25% {
		transform: scale(1) translate(0, 0);
	}

	100% {
		transform: scale(1) translate(0, 0);
	}
}

@keyframes bell-ring {
	0% {
		transform: rotate(0deg);
	}

	0.33%,
	0.99% {
		transform: rotate(25deg);
	}

	0.66%,
	1.32% {
		transform: rotate(-25deg);
	}

	1.66% {
		transform: rotate(0deg);
	}

	100% {
		transform: rotate(0deg);
	}
}
</style>
